import React, { Fragment, useEffect, useState } from "react";
import { InputAdornment, Tooltip } from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import CachedIcon from "@mui/icons-material/Cached";
import { createNewUser, fetchUserById, updateUser } from "../actions/user";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";
import InputField from "../components/Admin/Reusable/InputField";

import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";

import { useSelector } from "react-redux";
import { basicFormValidationHandler } from "../utils";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import { userActions } from "../reducers/user";
import GlobalProgressOverlay from "../components/Admin/Reusable/GlobalProgressOverlay";


function generateRandomPassword() {
  const length = Math.floor(Math.random() * 3) + 13; // Dužina između 9 i 11 karaktera
  const specialChars = "-_~.#!@&";

  const allChars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789" +
    specialChars;

  const getRandomChar = (charSet) => {
    const randomIndex = Math.floor(Math.random() * charSet.length);
    return charSet.charAt(randomIndex);
  };

  let password = "";
  password += getRandomChar("ABCDEFGHIJKLMNOPQRSTUVWXYZ"); // Prvo slovo je veliko
  password += getRandomChar("0123456789"); // Barem jedan broj
  password += getRandomChar(specialChars); // Barem jedan specijalni karakter

  while (password.length < length) {
    password += getRandomChar(allChars);
  }

  return password;
}

const UserAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const [errors, setErrors] = useState({});
  const { userDetail, loading, success, actionType } = useSelector((state) => state.userReducer);

  const initialState = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    birthday: "",
    active: true,
    newsletter: false,
  };

  //osnovi podaci
  const [data, setData] = useState(initialState);
  const [progress, setProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  //breadcrumbs
  const translateForm = "userForm";
  const translateModule = "user";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchUserById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (userDetail && id) {
      setData({
        ...userDetail,
      });
    }
  }, [userDetail, id, dispatch]);

  useEffect(() => {
      let interval;
      let timeout;
  
      if (loading) {
        setShowLoader(true);
        setProgress(0);
  
        interval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 100) {
              clearInterval(interval);
              return 100;
            }
            return prev + 5;
          });
        }, 100);
      } else if (!loading && showLoader) {
        setProgress(100);
        timeout = setTimeout(() => {
          setShowLoader(false);
          setProgress(0);
        }, 2000);
      }
  
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }, [loading]);
  
  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        navigate("/user-list");

        if (actionType === "create") {
          toast.success("Uspešno ste kreirali novog korisnika", { theme: "colored" });
        } else if (actionType === "update") {
          toast.success("Uspešno ste izmenili korisnika", { theme: "colored" });
        }

        dispatch(userActions.clearError());
      }, 2000);

      return () => clearTimeout(timeout);
    } else {
      dispatch(userActions.clearError());
    }
  }, [success]); 

  const userValidationHandler = () => {
    const { firstName, lastName, email, password } = data;

    let errorsObject = {};
    if (id) {
      errorsObject = basicFormValidationHandler({
        firstName,
        lastName,
        email,
      });
    } else {
      errorsObject = basicFormValidationHandler({
        firstName,
        lastName,
        email,
        password,
      });
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email.trim())) {
      errorsObject.email = "Email nije u dobrom formatu";
    }

    setErrors(errorsObject);
    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja!", {
        theme: "colored",
      });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (userValidationHandler()) {
      return;
    }

    if (id) {
      data.id = id;
    }

    if (id) {
      dispatch(updateUser(data, navigate, id));
    } else {
      dispatch(createNewUser(data, navigate));
    }
  };

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;

    setData({
      ...data,
      [name]: type === "checkbox" ? checked: name === "email" ? value.replace(/^\s+|\s+$/g, "") : value,
    });
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setData((prevUser) => ({
      ...prevUser,
      password: newPassword,
    }));
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        pageType={pageType}
      />
      {showLoader && <GlobalProgressOverlay progress={progress} />}
      <Container className="user-container">
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={9}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <div className="blog-holder-admin-left-side-text">
                            <Row>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.firstName`)}
                                  name="firstName"
                                  value={data.firstName}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.lastName`)}
                                  name="lastName"
                                  value={data.lastName}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.email`)}
                                  name="email"
                                  value={data.email}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              {!id && (
                                <Col lg={4} md={12} xs={12} className="mb-4">
                                  <InputField
                                    label={t(`${translateForm}.password`)}
                                    name="password"
                                    value={data.password}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required
                                    inputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Tooltip title="Generisi loziniku">
                                            <CachedIcon
                                              style={{ cursor: "pointer" }}
                                              onClick={() =>
                                                handleGeneratePassword()
                                              }
                                            />
                                          </Tooltip>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Col>
                              )}
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  type="date"
                                  label={t(`${translateForm}.birthday`)}
                                  name="birthday"
                                  value={data?.birthday}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  inputLabelProps={{ shrink: true }}
                                />
                              </Col>
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>{t(`global.settings`)}</Card.Header>
                        <Card.Body>
                          <SelectStatus
                            active={true}
                            data={data}
                            handleInputChange={handleInputChange}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UserAdd;
